import { formatEnvelopeDate } from 'modules/envelopes/utils/formatEnvelopeDate';

type TimelineStep = 'created' | 'sent' | 'awaiting' | 'started' | 'completed';

type EnvelopeTimelineItemProps = {
  step: TimelineStep;
  title: string;
  date: string | number | Date;
  description: string;
  isActive?: boolean;
  isCompleted?: boolean;
  uniformMarkers?: boolean;
};

const getMarkerConfig = (
  step: TimelineStep,
  uniformMarkers: boolean = false
) => {
  let config;
  switch (step) {
    case 'created':
      config = {
        className: 'bg-success',
        text: 'text-white',
        icon: 'ni ni-check-circle',
      };
      break;
    case 'sent':
      config = {
        className: 'bg-primary',
        text: 'text-white',
        icon: 'ni ni-send',
      };
      break;
    case 'awaiting':
      config = {
        className: 'bg-warning',
        text: 'text-white',
        icon: 'ni ni-clock',
      };
      break;
    case 'started':
      config = {
        className: 'bg-success',
        text: 'text-white',
        icon: 'ni ni-check-circle',
      };
      break;
    case 'completed':
      config = {
        className: 'bg-success',
        text: 'text-white',
        icon: 'ni ni-check-circle',
      };
      break;
    default:
      config = {
        className: 'bg-secondary',
        text: 'text-white',
        icon: 'ni ni-info',
      };
  }

  // If uniform markers is enabled, override only the color
  if (uniformMarkers) {
    return {
      ...config,
      className: 'bg-light',
      text: 'text-primary',
    };
  }

  return config;
};

/**
 * EnvelopeTimelineItem - A reusable timeline item component for envelope status steps
 *
 * Props differentiation for each step:
 *
 * 1. 'created' step:
 *    - Green success marker with check-circle icon
 *    - Represents envelope creation completion
 *    - Typically used with isCompleted=true
 *
 * 2. 'sent' step:
 *    - Blue primary marker with send icon
 *    - Represents envelope being sent to signers
 *    - Typically used with isActive=true
 *
 * 3. 'awaiting' step:
 *    - Yellow warning marker with clock icon
 *    - Represents waiting for signatures
 *    - isActive depends on current progress
 *
 * 4. 'started' step:
 *    - Green success marker with check-circle icon
 *    - Represents first signature completed
 *    - Only shown when completedSigners > 0
 *
 * 5. 'completed' step:
 *    - Green success marker with check-circle icon
 *    - Represents all signatures complete
 *    - Only shown when progressPercentage === 100
 *    - Typically used with isCompleted=true
 *
 * When uniformMarkers is true, all steps use bg-light color but keep their step-specific icons
 */
const EnvelopeTimelineItem = ({
  step,
  title,
  date,
  description,
  isActive = false,
  isCompleted = false,
  uniformMarkers = false,
}: EnvelopeTimelineItemProps) => {
  const markerConfig = getMarkerConfig(step, uniformMarkers);

  let textClass = 'text-muted';
  if (isCompleted) {
    textClass = 'text-muted';
  } else if (isActive) {
    textClass = 'text-soft';
  }

  return (
    <div className="timeline-item">
      <div
        className={`timeline-marker ${markerConfig.className} mr-2 rounded-circle text-center`}
      >
        <em
          className={`icon text-white ${markerConfig.icon} ${markerConfig.text}`}
        />
      </div>
      <div className="timeline-content">
        <h6 className="mb-1 fs-14px">{title}</h6>
        <p className={`${textClass} mb-1`}>{formatEnvelopeDate(date)}</p>
        <p className="text-muted ff-italic mb-0">
          <small>{description}</small>
        </p>
      </div>
    </div>
  );
};

export default EnvelopeTimelineItem;
